import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import TwoFactorSetupPanel from '../../components/auth/TwoFactorSetupPanel';
import Input from '../../components/common/Input';
import GradientButton from '../../components/common/GradientButton';
import useAuth from '../../hooks/useAuth';
import {
  getSecurityInfoApi,
  disableTwoFactorApi,
  regenerateBackupCodesApi,
  changePasswordApi,
} from '../../api/auth.api';
import { toLatinDigits } from '../../utils/nationalCode';

const fmt = (d) => {
  try {
    return new Intl.DateTimeFormat('fa-IR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(d));
  } catch {
    return String(d);
  }
};

const cleanCode = (v) => {
  const t = toLatinDigits(v).trim();
  return /^[\d\s]+$/.test(t) ? t.replace(/\s/g, '') : t;
};

function Section({ title, subtitle, children, tone = 'default' }) {
  return (
    <section
      className={`rounded-3xl border bg-white p-6 shadow-sm ${
        tone === 'good' ? 'border-emerald-200' : tone === 'warn' ? 'border-amber-200' : 'border-slate-200'
      }`}
    >
      <h2 className="text-lg font-black text-text-dark">{title}</h2>
      {subtitle && <p className="mt-1 text-sm leading-7 text-slate-500">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function SecuritySettings() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: 'success' });

  const [setupOpen, setSetupOpen] = useState(false);
  const [action, setAction] = useState(null); // 'disable' | 'backup'
  const [form, setForm] = useState({ password: '', code: '' });
  const [newCodes, setNewCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirm: '' });

  const notify = (text, type = 'success') => {
    setMessage({ text, type });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const load = useCallback(() => {
    setLoading(true);
    getSecurityInfoApi()
      .then(({ data }) => setInfo(data.data))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submitAction = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const code = cleanCode(form.code);
      if (action === 'disable') {
        await disableTwoFactorApi(form.password, code);
        notify('ورود دو مرحله‌ای غیرفعال شد. برای ادامه دوباره وارد شوید.');
        setTimeout(async () => {
          await logout();
          navigate('/login');
        }, 1500);
      } else {
        const { data } = await regenerateBackupCodesApi(form.password, code);
        setNewCodes(data.data.backupCodes);
        notify('کدهای پشتیبان جدید ساخته شد؛ کدهای قبلی باطل شدند.');
        load();
      }
      setAction(null);
      setForm({ password: '', code: '' });
    } catch (err) {
      notify(err?.response?.data?.message || 'عملیات ناموفق بود', 'error');
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pw.newPassword !== pw.confirm) {
      notify('تکرار رمز عبور جدید یکسان نیست', 'error');
      return;
    }
    setBusy(true);
    try {
      await changePasswordApi(pw.currentPassword, pw.newPassword);
      notify('رمز عبور تغییر کرد. دوباره وارد شوید.');
      setTimeout(async () => {
        await logout();
        navigate('/login');
      }, 1500);
    } catch (err) {
      const first = err?.response?.data?.errors?.[0]?.message;
      notify(first || err?.response?.data?.message || 'تغییر رمز ناموفق بود', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="py-16 text-center text-slate-400">در حال بارگذاری...</div>;
  if (!info) return <div className="py-16 text-center text-slate-400">دریافت اطلاعات امنیتی ناموفق بود</div>;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-black text-text-dark">امنیت حساب</h1>
        <p className="mt-1 text-sm text-slate-400">ورود دو مرحله‌ای، رمز عبور و سابقه‌ی ورودها</p>
      </div>

      {message.text && (
        <div
          className={`rounded-xl p-3 text-sm font-bold ${
            message.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* ---------- 2FA ---------- */}
      <Section
        tone={info.twoFactorEnabled ? 'good' : 'warn'}
        title={info.twoFactorEnabled ? '🔐 ورود دو مرحله‌ای فعال است' : '⚠️ ورود دو مرحله‌ای فعال نیست'}
        subtitle={
          info.twoFactorEnabled
            ? `فعال‌شده در ${fmt(info.twoFactorEnabledAt)} • کدهای پشتیبان باقی‌مانده: ${info.backupCodesRemaining}`
            : 'با فعال‌سازی، حتی اگر رمز عبور شما لو برود، بدون کد برنامه‌ی احراز هویت کسی نمی‌تواند وارد حساب شما شود.'
        }
      >
        {!info.twoFactorEnabled && !setupOpen && (
          <GradientButton onClick={() => setSetupOpen(true)}>فعال‌سازی ورود دو مرحله‌ای</GradientButton>
        )}

        {!info.twoFactorEnabled && setupOpen && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <TwoFactorSetupPanel
              onCancel={() => setSetupOpen(false)}
              onDone={() => {
                setSetupOpen(false);
                notify('ورود دو مرحله‌ای فعال شد. از ورود بعدی، کد برنامه لازم است.');
                // نشست‌های قبلی باطل شده‌اند؛ برای ورود دوباره از سیستم خارج می‌شویم
                setTimeout(async () => {
                  await logout();
                  navigate('/login');
                }, 1500);
              }}
            />
          </div>
        )}

        {info.twoFactorEnabled && !action && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setAction('backup')}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
              ساخت کدهای پشتیبان جدید
            </button>
            {info.twoFactorRequired ? (
              <span className="rounded-xl bg-slate-100 px-4 py-2 text-xs font-bold text-slate-500">
                برای نقش شما اجباری است و قابل غیرفعال‌سازی نیست
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setAction('disable')}
                className="rounded-xl bg-red-50 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-100"
              >
                غیرفعال‌سازی
              </button>
            )}
          </div>
        )}

        {action && (
          <form onSubmit={submitAction} className="max-w-sm space-y-3">
            <p className="text-sm text-slate-500">
              {action === 'disable' ? 'برای غیرفعال‌سازی' : 'برای ساخت کدهای جدید'} رمز عبور و یک کد معتبر (برنامه یا پشتیبان) را وارد کنید.
            </p>
            <Input
              label="رمز عبور"
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <Input
              label="کد دو مرحله‌ای"
              required
              dir="ltr"
              autoComplete="one-time-code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              className="text-left font-mono"
            />
            <div className="flex gap-2">
              <GradientButton type="submit" disabled={busy}>
                {busy ? 'در حال انجام...' : 'تأیید'}
              </GradientButton>
              <button type="button" onClick={() => setAction(null)} className="px-3 text-sm font-bold text-slate-500">
                انصراف
              </button>
            </div>
          </form>
        )}

        {newCodes && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="mb-2 text-sm font-black text-amber-800">کدهای پشتیبان جدید (فقط همین یک بار نمایش داده می‌شود)</p>
            <div dir="ltr" className="grid grid-cols-2 gap-2 font-mono text-sm tracking-wider text-slate-800">
              {newCodes.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
            <button
              type="button"
              onClick={() => navigator.clipboard?.writeText(newCodes.join('\n'))}
              className="mt-3 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-white"
            >
              کپی کدها
            </button>
          </div>
        )}
      </Section>

      {/* ---------- Password ---------- */}
      <Section title="تغییر رمز عبور" subtitle="رمز جدید باید حداقل ۸ کاراکتر و شامل حرف و عدد باشد. پس از تغییر، از همه‌ی دستگاه‌ها خارج می‌شوید.">
        <form onSubmit={submitPassword} className="grid max-w-xl gap-3 md:grid-cols-2">
          <Input
            label="رمز عبور فعلی"
            type="password"
            required
            value={pw.currentPassword}
            onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })}
            className="md:col-span-2"
          />
          <Input
            label="رمز عبور جدید"
            type="password"
            required
            value={pw.newPassword}
            onChange={(e) => setPw({ ...pw, newPassword: e.target.value })}
          />
          <Input
            label="تکرار رمز جدید"
            type="password"
            required
            value={pw.confirm}
            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
          />
          <div className="md:col-span-2">
            <GradientButton type="submit" disabled={busy}>
              تغییر رمز عبور
            </GradientButton>
          </div>
        </form>
      </Section>

      {/* ---------- history ---------- */}
      <Section title="آخرین ورودها" subtitle="اگر ورودی را نمی‌شناسید، فوراً رمز عبور خود را تغییر دهید.">
        <ul className="divide-y divide-slate-100 text-sm">
          {info.recentLogins.length === 0 && <li className="py-3 text-slate-400">سابقه‌ای ثبت نشده است</li>}
          {info.recentLogins.map((l) => (
            <li key={l.id} className="flex flex-wrap items-center gap-3 py-3">
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-black ${
                  l.status === 'SUCCESS' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
                }`}
              >
                {l.status === 'SUCCESS' ? 'موفق' : 'ناموفق'}
              </span>
              <span className="text-slate-600">{fmt(l.createdAt)}</span>
              <span className="font-mono text-xs text-slate-400" dir="ltr">{l.ipAddress || '—'}</span>
              {l.reason === 'BACKUP_CODE' && <span className="text-xs text-amber-600">با کد پشتیبان</span>}
              {l.reason === 'BAD_PASSWORD' && <span className="text-xs text-slate-400">رمز اشتباه</span>}
              {l.reason === 'BAD_2FA_CODE' && <span className="text-xs text-slate-400">کد دو مرحله‌ای اشتباه</span>}
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}
